import { Component, OnInit } from '@angular/core';
import { ActionHistoryService, ActionHistory } from 'src/app/services/actionHistory/action-history.service';

@Component({
  selector: 'app-action-history',
  templateUrl: './action-history.component.html',
  styleUrls: ['./action-history.component.css']
})
export class ActionHistoryComponent implements OnInit {
  actions: ActionHistory[] = [];
  actionTypes: string[] = [];
  selectedActionType = '';
  selectedEntityType = '';
  selectedDate = '';
  searchTerm = '';
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  selectedAction: ActionHistory | null = null;
  isLoading = false;
  detailsOrderNumber = 'N/A';
  detailsSiniestro = 'N/A';
  loadingDetailsInfo = false;

  constructor(private actionHistoryService: ActionHistoryService) {
  }

  ngOnInit(): void {
    this.loadActionTypes();
    this.loadActions();
  }

  loadActionTypes() {
    this.actionHistoryService.getActionTypes().subscribe({
      next: types => {
        this.actionTypes = types || [];
      },
      error: error => {
        console.error('Error cargando tipos de acción:', error);
        this.actionTypes = [];
      }
    });
  }

  private loadActions() {
    this.isLoading = true;
    this.actionHistoryService.getPaginatedActionHistory({
      page: this.currentPage,
      pageSize: this.pageSize,
      search: this.searchTerm,
      actionType: this.selectedActionType,
      entityType: this.selectedEntityType,
      date: this.selectedDate
    }).subscribe({
      next: response => {
        this.actions = response.data || [];
        this.totalItems = response.totalCount || 0;
        this.totalPages = response.totalPages || 0;
        this.isLoading = false;
      },
      error: error => {
        console.error('Error cargando historial:', error);
        this.actions = [];
        this.totalItems = 0;
        this.totalPages = 0;
        this.isLoading = false;
      }
    });
  }

  onSearch() {
    this.currentPage = 1;
    this.loadActions();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadActions();
  }

  getActionTypeClass(actionType: string): string {
    switch (actionType) {
      case 'CREATE':
        return 'badge badge-success';      // Verde
      case 'UPDATE':
        return 'badge badge-warning';      // Amarillo
      case 'DELETE':
        return 'badge badge-error';        // Rojo
      case 'BULK_UPDATE_STATUS':
        return 'badge badge-info';         // Azul
      default:
        return 'badge badge-neutral';      // Gris
    }
  }

  entityType(entityType: string) {
    switch (entityType) {
      case 'Order':
        return 'Orden';
      case 'SparePart':
        return 'Repuesto';
      case 'Workshop':
        return 'Taller';
      case 'PackageList':
        return 'Lista Paquetes';
      case 'PackageListItem':
        return 'Lista Items Paquetes';
      case 'ExportedFolder':
        return 'Carpeta Exportada';
      default:
        return entityType;
    }
  }

  showDetails(action: ActionHistory) {
    this.selectedAction = action;
    this.detailsOrderNumber = '';
    this.detailsSiniestro = '';
    this.loadingDetailsInfo = true;
    this.loadDetailsOrderInfo(action);
  }

  closeDetails() {
    this.selectedAction = null;
    this.detailsOrderNumber = 'N/A';
    this.detailsSiniestro = 'N/A';
    this.loadingDetailsInfo = false;
  }

  getOrderNumber(action: ActionHistory): string {
    const data = this.parseAdditionalData(action);
    const value =
      data?.orderNumber ??
      data?.OrderNumber ??
      data?.ordernumber ??
      data?.order?.orderNumber ??
      data?.Order?.OrderNumber ??
      data?.order?.OrderNumber ??
      data?.Order?.orderNumber;
    return value || 'N/A';
  }

  getSiniestro(action: ActionHistory): string {
    const data = this.parseAdditionalData(action);
    const value =
      data?.siniestro ??
      data?.Siniestro ??
      data?.siniestroNumber ??
      data?.order?.siniestro ??
      data?.Order?.Siniestro ??
      data?.order?.Siniestro ??
      data?.Order?.siniestro;
    return value || 'N/A';
  }

  private parseAdditionalData(action: ActionHistory): any | null {
    if (!action?.additionalData) {
      return null;
    }
    if (typeof action.additionalData === 'object') {
      return action.additionalData;
    }
    if (typeof action.additionalData === 'string') {
      try {
        return JSON.parse(action.additionalData);
      } catch {
        return null;
      }
    }
    return null;
  }

  private loadDetailsOrderInfo(action: ActionHistory) {
    if (!action || !action.entityType || !action.entityId) {
      this.detailsOrderNumber = this.getOrderNumber(action);
      this.detailsSiniestro = this.getSiniestro(action);
      this.loadingDetailsInfo = false;
      return;
    }
    const entityType = action.entityType;
    const entityId = action.entityId;
    if (entityType !== 'Order' && entityType !== 'SparePart') {
      this.detailsOrderNumber = this.getOrderNumber(action);
      this.detailsSiniestro = this.getSiniestro(action);
      this.loadingDetailsInfo = false;
      return;
    }
    this.actionHistoryService.getEntityOrderInfo(entityType, entityId).subscribe({
      next: info => {
        const fallbackOrder = this.getOrderNumber(action);
        const fallbackSiniestro = this.getSiniestro(action);
        this.detailsOrderNumber = info?.orderNumber || (fallbackOrder !== 'N/A' ? fallbackOrder : '');
        this.detailsSiniestro = info?.siniestro || (fallbackSiniestro !== 'N/A' ? fallbackSiniestro : '');
        this.loadingDetailsInfo = false;
      },
      error: () => {
        this.detailsOrderNumber = this.getOrderNumber(action);
        this.detailsSiniestro = this.getSiniestro(action);
        this.loadingDetailsInfo = false;
      }
    });
  }

  get formattedAdditionalData(): string {
    try {
      if (typeof this.selectedAction?.additionalData === 'string') {
        const obj = JSON.parse(this.selectedAction.additionalData);
        return JSON.stringify(obj, null, 1);
      }
      if (typeof this.selectedAction?.additionalData === 'object') {
        return JSON.stringify(this.selectedAction.additionalData, null, 1);
      }
      return '';
    } catch (e) {
      return this.selectedAction?.additionalData || '';
    }
  }

  /** Etiquetas amigables por entidad (campo técnico → español) */
  private static readonly FIELD_LABELS: Record<string, Record<string, string>> = {
    SparePart: {
      SparePartName: 'Nombre del repuesto',
      SparePartNameEs: 'Nombre (español)',
      SparePartNamePt: 'Nombre (portugués)',
      ProductCode: 'Código de producto',
      Amount: 'Cantidad',
      UnitPrice: 'Precio unitario',
      ValorDeVenta: 'Valor de venta',
      TaxNote: 'Nota fiscal',
      NCM: 'NCM',
      ProductStatus: 'Estado del producto',
      ExpectedArrivalDate: 'Fecha de llegada',
      Comments: 'Comentarios',
      IsDamaged: 'Dañado',
      CodBR: 'Código BR',
      NetWheight: 'Peso neto',
      GrossWheight: 'Peso bruto',
      Country: 'País',
      NeedReview: 'Requiere revisión',
      ShippingStatus: 'Estado de envío',
      ShippingReceipt: 'Remito'
    },
    Order: {
      OrderNumber: 'Orden',
      ProviderName: 'Proveedor',
      ProviderPhone: 'Tel. proveedor',
      ClientName: 'Cliente',
      ClientPhone: 'Tel. cliente',
      InvoiceNumber: 'Factura',
      EmployeeName: 'Vendedor',
      Siniestro: 'Siniestro',
      DeliveryAddress: 'Dirección de entrega',
      CarMakeModel: 'Vehículo',
      IsExport: 'Es exportación',
      OrderStatus: 'Estado',
      SendYesOrNo: 'Enviar',
      IsNearArrival: 'Cerca de llegada',
      IsNearArrivalAccepted: 'Llegada aceptada',
      ClienteUY: 'Cliente UY',
      WorkshopId: 'Taller',
      ExpectedArrivalDate: 'Fecha de llegada'
    },
    Workshop: {
      Name: 'Nombre',
      Address: 'Dirección',
      PhoneNumber: 'Teléfono',
      Department: 'Departamento',
      City: 'Ciudad',
      Company: 'Empresa'
    }
  };

  /** Convierte camelCase a PascalCase para coincidir con FIELD_LABELS (ej: clientName → ClientName). */
  private toPascalCase(str: string): string {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /** Resuelve etiqueta amigable para un campo (soporta clave en PascalCase o camelCase del API). */
  private getFieldLabel(entityType: string, key: string): string {
    const labels = ActionHistoryComponent.FIELD_LABELS[entityType] || {};
    return labels[key] ?? labels[this.toPascalCase(key)] ?? key;
  }

  /** Cambios parseados para mostrar en la vista (campo, etiqueta, valor anterior, valor nuevo). Misma lógica para todas las entidades. */
  get parsedChanges(): { fieldLabel: string; from: string; to: string }[] {
    const data = this.parseAdditionalData(this.selectedAction!);
    if (!data) return [];
    const changesObj = data.Changes ?? data.changes ?? data;
    if (typeof changesObj !== 'object' || changesObj === null) return [];
    const entityType = this.selectedAction?.entityType || '';
    const result: { fieldLabel: string; from: string; to: string }[] = [];
    for (const [key, val] of Object.entries(changesObj)) {
      if (val && typeof val === 'object') {
        const v = val as Record<string, unknown>;
        const fromVal = v.From ?? v.from;
        const toVal = v.To ?? v.to;
        if (fromVal !== undefined || toVal !== undefined) {
          result.push({
            fieldLabel: this.getFieldLabel(entityType, key),
            from: this.formatChangeValue(fromVal),
            to: this.formatChangeValue(toVal)
          });
        }
      }
    }
    return result;
  }

  /** Datos de creación parseados (solo campo + valor, mismo estilo que UPDATE pero sin "anterior → nuevo"). */
  get parsedCreateData(): { fieldLabel: string; value: string }[] {
    if (this.selectedAction?.actionType !== 'CREATE') return [];
    const data = this.parseAdditionalData(this.selectedAction);
    if (!data || typeof data !== 'object') return [];
    if (this.parsedChanges.length > 0) return [];
    const entityType = this.selectedAction.entityType || '';
    const result: { fieldLabel: string; value: string }[] = [];
    for (const [key, val] of Object.entries(data)) {
      if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
        const v = val as Record<string, unknown>;
        if (v.From !== undefined || v.from !== undefined || v.To !== undefined || v.to !== undefined) continue;
      }
      result.push({
        fieldLabel: this.getFieldLabel(entityType, key),
        value: this.formatChangeValue(val)
      });
    }
    return result;
  }

  private formatChangeValue(val: unknown): string {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'Sí' : 'No';
    if (typeof val === 'number') return Number.isInteger(val) ? String(val) : (val as number).toFixed(2);
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
      try {
        const d = new Date(val);
        if (!isNaN(d.getTime())) return d.toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' });
      } catch { }
    }
    return String(val).trim() || '—';
  }
}
